
import { UseFormReturn } from "react-hook-form";
import { useState } from "react";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { CountryCodeSelect } from "../CountryCodeSelect";
import { CustomerFormData } from "./types";
import { validateEmailTLD, EmailValidationResult } from "@/utils/emailValidation";
import { AlertTriangle, Info } from "lucide-react";

interface PersonalInfoFieldsProps {
  form: UseFormReturn<CustomerFormData>;
  eventCurrency?: string;
}

export const PersonalInfoFields = ({ form, eventCurrency }: PersonalInfoFieldsProps) => {
  const [emailValidation, setEmailValidation] = useState<EmailValidationResult | null>(null);

  const handleEmailChange = (value: string) => {
    if (value && value.includes('@')) {
      const validation = validateEmailTLD(value);
      setEmailValidation(validation);
    } else {
      setEmailValidation(null);
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    form.setValue('email', suggestion);
    setEmailValidation(null);
  };

  const handlePhoneNumberChange = (value: string) => {
    // Remove all non-numeric characters
    const numericValue = value.replace(/[^0-9]/g, '');
    return numericValue;
  };

  const handlePhoneNumberBlur = (value: string) => {
    // Remove leading zeros when user clicks out
    const trimmedValue = value.replace(/^0+/, '');
    return trimmedValue || '0'; // Keep at least one zero if the field would be empty
  };

  return (
    <>
      <div className="grid grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="firstName"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-white">First Name</FormLabel>
              <FormControl>
                <Input placeholder="Cher" {...field} className="bg-white text-black" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="lastName"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-white">Last Name</FormLabel>
              <FormControl>
                <Input placeholder="Sarkisian" {...field} className="bg-white text-black" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      
      <FormField
        control={form.control}
        name="email"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-white">Email</FormLabel>
            <FormControl>
              <Input 
                placeholder="cher@gmail.com" 
                type="email" 
                {...field}
                onChange={(e) => {
                  field.onChange(e);
                  handleEmailChange(e.target.value);
                }}
                className="bg-white text-black"
              />
            </FormControl>
            {emailValidation && emailValidation.warning && (
              <div className={`mt-2 p-3 rounded-md border ${
                emailValidation.type === 'suspicious' 
                  ? 'bg-red-50 border-red-200 text-red-700' 
                  : emailValidation.type === 'suggestion'
                  ? 'bg-blue-50 border-blue-200 text-blue-700'
                  : 'bg-yellow-50 border-yellow-200 text-yellow-700'
              }`}>
                <div className="flex items-start gap-2">
                  {emailValidation.type === 'suspicious' ? (
                    <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  ) : (
                    <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  )}
                  <div className="flex-1">
                    <p className="text-sm">{emailValidation.warning}</p>
                    {emailValidation.suggestion && (
                      <button
                        type="button"
                        onClick={() => handleSuggestionClick(emailValidation.suggestion!)}
                        className="mt-1 text-sm font-medium underline hover:no-underline"
                      >
                        Use {emailValidation.suggestion} instead
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
            <FormMessage />
          </FormItem>
        )}
      />

      <div className="flex gap-4">
        <FormField
          control={form.control}
          name="phoneCountryCode"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-white">Code</FormLabel>
              <FormControl>
                <CountryCodeSelect
                  value={field.value}
                  onValueChange={field.onChange}
                  eventCurrency={eventCurrency}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="phoneNumber"
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormLabel className="text-white">Phone Number</FormLabel>
              <FormControl>
                <Input 
                  placeholder="412345678" 
                  type="tel" 
                  {...field}
                  onChange={(e) => {
                    const numericValue = handlePhoneNumberChange(e.target.value);
                    field.onChange(numericValue);
                  }}
                  onBlur={(e) => {
                    const trimmedValue = handlePhoneNumberBlur(e.target.value);
                    field.onChange(trimmedValue);
                  }}
                  className="bg-white text-black" 
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </>
  );
};
